import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { useAvailabilityGuard } from '@/hooks/useAvailabilityGuard'
import { useGooglePlacesAutocomplete, type ParsedPlace } from '@/hooks/useGooglePlacesAutocomplete'
import {
  createContact,
  createContactAddress,
  saveCheckoutSession,
  syncCheckoutSession,
  updateContactAddress,
  updateContactProperties,
} from '@/lib/ecommerce/api'
import {
  checkoutStorage,
  consumeInternalCheckoutBack,
  isBackForwardNavigation,
  isInternalCheckoutBack,
} from '@/lib/ecommerce/checkoutStorage'
import {
  CheckoutPageShell,
  CheckoutFieldRow,
  CHECKOUT_INPUT_CLASS,
  CHECKOUT_SUBMIT_CLASS,
} from '@/components/ecommerce/CheckoutSteps'
import FlipButton from '@/components/ui/FlipButton'
import {
  SHIPPING_OPTION_ORDER,
  SHIPPING_OPTIONS,
  getShippingOption,
  isPickupOption,
  isGreaterSydneyPostcode,
  SYDNEY_OUT_OF_AREA_MESSAGE,
  SHIPPING_OPTION_EVENT,
} from '@/lib/ecommerce/shippingOptions'
import type { CheckoutShippingPagePayload } from '@/types/ecommerce'
import type { InsInputTelElement } from '@/types/insites-components'

/*
  Checkout Step 2 — Shipping (Teamwork 26100596).

  Mirrors the ecommerce add-on's flow with MIG styling:
    1. "Same as contact" checkbox (default ON) — when ON, the shipping
       contact reuses the order contact's name/email/phone/company.
       When OFF, the user enters a separate shipping contact and we
       create a new CRM contact for them on submit.
    2. Search Address (Google Places Autocomplete, AU-restricted) +
       individual address fields (Address 1, Address 2, Suburb, State,
       Postcode, Country — defaults to Australia).
    3. Shipping Instructions (optional textarea).
    4. Continue to Billing — creates the CRM address tied to the
       resolved contact UUID, saves shipping fields onto the addon
       checkout session, then routes to /checkout/billing.

  Data persistence on Back: every keystroke writes a draft into
  sessionStorage; on mount, if a draft exists we restore it (this is
  layered on top of the addon's server-side session, which is what the
  Liquid wrapper pre-fills from). That way "go back to /checkout, edit,
  return" doesn't lose what was typed in shipping.
*/

const DRAFT_KEY = 'mig-checkout-shipping-draft'

interface ShippingForm {
  // Chosen shipping method (UAT-36). '' until the buyer picks one — a
  // method must be selected before Continue.
  shipping_option: string
  same_as_contact: boolean
  // Shipping contact (only sent when same_as_contact is false)
  ship_company_name: string
  ship_first_name: string
  ship_last_name: string
  ship_email: string
  ship_phone: string
  ship_phone_country_code: string
  // Address — either a picked saved CRM address (selected_address_id is
  // non-empty) or a new one being typed in (selected_address_id === '').
  // Mirrors the addon's checkout_shipping_address form, which renders
  // saved address cards for logged-in users and only opens the entry
  // fields when "Add new address" is clicked.
  selected_address_id: string
  address_1: string
  address_2: string
  suburb: string
  state: string
  postcode: string
  country: string
  // Instructions
  instructions: string
}

interface FieldErrors {
  shipping_option?: string
  ship_first_name?: string
  ship_last_name?: string
  ship_email?: string
  ship_phone?: string
  address_1?: string
  suburb?: string
  state?: string
  postcode?: string
  country?: string
}

function FieldError({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <p id={id} role="alert" className="mig-checkout-field-error">
      {children}
    </p>
  )
}

function SectionHeading({
  children,
  spacing,
}: {
  children: React.ReactNode
  spacing?: 8 | 32 | 48
}) {
  return (
    <h2 className="mig-checkout-section-heading" data-spacing={spacing}>
      {children}
    </h2>
  )
}

/* Saved-address selector card. Mirrors the addon's address_cards
   partial: shows the joined street line + locality + country. Selected
   state shows a filled black circle with a white check on the left
   (matching the saved-card radio on /checkout/payment) plus a black
   2px border around the whole card. */
function AddressCard({
  primary,
  secondary,
  tertiary,
  label,
  selected,
  disabled,
  onSelect,
  hideIndicator,
}: {
  primary: string
  secondary: string
  tertiary?: string
  label?: string
  selected: boolean
  disabled?: boolean
  onSelect: () => void
  /** Skip rendering the radio-style indicator on the left. Used for
   *  the "+ Add new address" card which represents an action rather
   *  than a saved option. */
  hideIndicator?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className="mig-checkout-address-card"
      data-selected={selected}
    >
      {!hideIndicator && (
        <span className="mig-checkout-address-card-indicator" aria-hidden="true" />
      )}
      <span className="mig-checkout-address-card-content">
        {label && <span className="mig-checkout-address-card-label">{label}</span>}
        <span className="mig-checkout-address-card-primary">{primary}</span>
        {secondary && <span className="mig-checkout-address-card-line">{secondary}</span>}
        {tertiary && <span className="mig-checkout-address-card-line">{tertiary}</span>}
      </span>
    </button>
  )
}

export default function CheckoutShipping() {
  // Race-safe sold-item eviction.
  useAvailabilityGuard()
  const payload = usePageData<CheckoutShippingPagePayload>() ?? {
    is_authenticated: false,
    user_uuid: null,
    session_id: null,
    contact_email: null,
    shipping_option: null,
    contact_uuid: null,
    contact_first_name: '',
    contact_last_name: '',
    contact_phone: '',
    contact_phone_country_code: '+61',
    contact_company_name: '',
    shipping_same_with_account: true,
    shipping_company_name: '',
    shipping_first_name: '',
    shipping_last_name: '',
    shipping_email: '',
    shipping_phone: '',
    shipping_phone_country_code: '+61',
    shipping_address_1: '',
    shipping_address_2: '',
    shipping_suburb: '',
    shipping_state: '',
    shipping_postcode: '',
    shipping_country: 'Australia',
    shipping_instructions: '',
    saved_addresses: [],
    shipping_address_id: null,
  }

  // Defensive: Liquid can occasionally hand us a JSON string instead of
  // an array if the wrapper code is changed and a `parse_json` filter
  // gets dropped. Guard with Array.isArray so a wrong shape can't take
  // the entire page down again.
  const savedAddresses = Array.isArray(payload.saved_addresses) ? payload.saved_addresses : []

  const initialForm: ShippingForm = useMemo(() => {
    // Resolve which saved address (if any) should be pre-selected. Order
    // of preference: previous session pick → first saved address.
    let selectedId = ''
    if (savedAddresses.length > 0) {
      const sessionId = payload.shipping_address_id
      if (sessionId && savedAddresses.some((a) => a.id === sessionId)) {
        selectedId = sessionId
      } else {
        selectedId = savedAddresses[0].id
      }
    }
    return {
      shipping_option: getShippingOption(payload.shipping_option)?.key ?? '',
      same_as_contact: payload.shipping_same_with_account ?? true,
      ship_company_name: payload.shipping_company_name ?? '',
      ship_first_name: payload.shipping_first_name ?? '',
      ship_last_name: payload.shipping_last_name ?? '',
      ship_email: payload.shipping_email ?? '',
      ship_phone: payload.shipping_phone ?? '',
      ship_phone_country_code: payload.shipping_phone_country_code ?? '+61',
      selected_address_id: selectedId,
      address_1: payload.shipping_address_1 ?? '',
      address_2: payload.shipping_address_2 ?? '',
      suburb: payload.shipping_suburb ?? '',
      state: payload.shipping_state ?? '',
      postcode: payload.shipping_postcode ?? '',
      country: payload.shipping_country || 'Australia',
      instructions: payload.shipping_instructions ?? '',
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [state, setState] = useState<ShippingForm>(() => {
    if (typeof window === 'undefined') return initialForm

    // Restore the draft when the browser navigated here via back /
    // forward OR the previous page's in-page BACK link set the
    // `mig-checkout-internal-back` flag — same restore semantics
    // either way. The flag is one-shot (consumed here). Other entry
    // paths clear the draft and use `initialForm` instead.
    const cameFromInPageBack = isInternalCheckoutBack()
    if (cameFromInPageBack) consumeInternalCheckoutBack()
    if (!isBackForwardNavigation() && !cameFromInPageBack) {
      try {
        window.sessionStorage.removeItem(DRAFT_KEY)
      } catch {
        // sessionStorage may throw in private windows / quota — silent.
      }
      return initialForm
    }

    try {
      const raw = window.sessionStorage.getItem(DRAFT_KEY)
      if (!raw) return initialForm
      const parsed = JSON.parse(raw)
      const merged: ShippingForm = { ...initialForm, ...parsed }
      // Self-heal stale draft: if the persisted `selected_address_id`
      // doesn't match any address currently in `savedAddresses`, reset
      // it. Without this, a draft saved during a previous render where
      // the user picked a saved card can leave the page in a blank
      // state on the next visit if the cards have since gone away
      // (e.g. an earlier experimental Liquid change that exposed
      // cards to guests was reverted but the draft survives). When
      // `selected_address_id` is truthy AND there's no matching card,
      // both the cards section and the new-address form are
      // conditionally hidden — leaving the user staring at headings
      // with nothing between them.
      if (
        merged.selected_address_id &&
        !savedAddresses.some((a) => a.id === merged.selected_address_id)
      ) {
        merged.selected_address_id = ''
      }
      // Self-heal a stale/unknown shipping option from an older draft so
      // a removed/renamed option key can't leave the page in a state the
      // selector can't represent.
      if (merged.shipping_option && !getShippingOption(merged.shipping_option)) {
        merged.shipping_option = ''
      }
      // A pickup option never carries a saved-address selection; if a
      // draft somehow has both (e.g. user picked an address then switched
      // to pickup), drop the address pick so the address section stays
      // hidden and no address is sent.
      if (isPickupOption(merged.shipping_option) && merged.selected_address_id) {
        merged.selected_address_id = ''
      }
      return merged
    } catch {
      return initialForm
    }
  })
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const telRef = useRef<InsInputTelElement | null>(null)

  // Persist draft on every change so navigating back/forward survives.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(state))
    } catch {
      // sessionStorage can throw in private windows / quota — silent.
    }
  }, [state])

  function update<K extends keyof ShippingForm>(key: K, value: ShippingForm[K]) {
    setState((prev) => {
      const next = { ...prev, [key]: value }
      // Switching to a pickup option clears any saved-address selection so
      // the address section stays hidden and no address is attached.
      if (key === 'shipping_option' && isPickupOption(value as string)) {
        next.selected_address_id = ''
      }
      return next
    })
    // Tell the order-summary right rail to update its shipping line +
    // total immediately as the buyer clicks an option (before submit).
    if (key === 'shipping_option' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SHIPPING_OPTION_EVENT, { detail: value as string }))
    }
  }

  const selectedOption = getShippingOption(state.shipping_option)
  const optionIsPickup = !!selectedOption?.isPickup
  // The address block only shows for delivery options. Until a method is
  // chosen, no address fields render (the selector comes first).
  const showAddressSection = !!selectedOption && selectedOption.requiresAddress

  function onPlaceSelected(place: ParsedPlace) {
    setState((prev) => ({
      ...prev,
      address_1: place.address_1 || prev.address_1,
      address_2: place.address_2 || prev.address_2,
      suburb: place.suburb || prev.suburb,
      state: place.state || prev.state,
      postcode: place.postcode || prev.postcode,
      country: place.country || prev.country,
    }))
  }

  // Only bind the autocomplete in "add new address" mode — when a saved
  // card is selected the search input isn't rendered, so binding would
  // be a no-op anyway, but keeping the hook gated avoids holding onto a
  // listener for an input that may unmount and remount as the user
  // toggles between cards and the entry form.
  useGooglePlacesAutocomplete(searchInputRef, onPlaceSelected, {
    country: 'au',
    enabled: showAddressSection && !state.selected_address_id,
  })

  function validate(s: ShippingForm): FieldErrors {
    const fe: FieldErrors = {}

    const option = getShippingOption(s.shipping_option)
    if (!option) {
      fe.shipping_option = 'Please choose a shipping method.'
      // Without a method we can't sensibly validate the rest; the option
      // error alone is enough to block Continue.
      return fe
    }

    if (!s.same_as_contact) {
      if (!s.ship_first_name.trim()) fe.ship_first_name = 'First name is required.'
      if (!s.ship_last_name.trim()) fe.ship_last_name = 'Last name is required.'
      if (!s.ship_email.trim()) fe.ship_email = 'Email is required.'
      else if (!/^\S+@\S+\.\S+$/.test(s.ship_email.trim())) fe.ship_email = 'Please enter a valid email.'
      // Phone is required for every option (UAT-36). When the buyer
      // enters a separate shipping recipient, require it on this field.
      if (!s.ship_phone.trim()) fe.ship_phone = 'Phone number is required.'
    }

    // Address + postcode only apply to delivery options with no saved
    // card picked. Pickups collect no address at all.
    if (option.requiresAddress && !s.selected_address_id) {
      if (!s.address_1.trim()) fe.address_1 = 'Address Line 1 is required.'
      if (!s.suburb.trim()) fe.suburb = 'Suburb is required.'
      if (!s.state.trim()) fe.state = 'State is required.'
      if (!s.postcode.trim()) fe.postcode = 'Postcode is required.'
      if (!s.country.trim()) fe.country = 'Country is required.'

      // Greater Sydney postcode gate — sydney_delivery only.
      if (option.key === 'sydney_delivery' && s.postcode.trim() && !isGreaterSydneyPostcode(s.postcode)) {
        fe.postcode = SYDNEY_OUT_OF_AREA_MESSAGE
      }
    }

    // For a Sydney delivery using a SAVED address card, still gate the
    // card's postcode (the card's fields are the resolved address).
    if (option.key === 'sydney_delivery' && s.selected_address_id) {
      const picked = savedAddresses.find((a) => a.id === s.selected_address_id)
      if (picked && !isGreaterSydneyPostcode(picked.postcode ?? '')) {
        fe.postcode = SYDNEY_OUT_OF_AREA_MESSAGE
      }
    }
    return fe
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    // Read the phone widget for shipping contact (when not same-as-contact).
    let phoneFromTel = state.ship_phone
    let codeFromTel = state.ship_phone_country_code
    if (!state.same_as_contact && telRef.current?.getValues) {
      try {
        const vals = await telRef.current.getValues()
        phoneFromTel = vals.phone_number || phoneFromTel
        codeFromTel = vals.country_code ? `+${vals.country_code}` : codeFromTel
      } catch {
        // fall through to the raw state values
      }
    }

    const submittingState: ShippingForm = {
      ...state,
      ship_phone: phoneFromTel,
      ship_phone_country_code: codeFromTel,
    }

    const submittingOption = getShippingOption(submittingState.shipping_option)

    // Pickup options collect no address. If the buyer typed an address
    // then switched to a pickup before submitting, blank the address
    // fields here so nothing stale gets attached to the order.
    if (submittingOption?.isPickup) {
      submittingState.selected_address_id = ''
      submittingState.address_1 = ''
      submittingState.address_2 = ''
      submittingState.suburb = ''
      submittingState.state = ''
      submittingState.postcode = ''
      submittingState.country = ''
    }

    const fieldErrors = validate(submittingState)
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) {
      setSubmitError(null)
      return
    }

    // Phone is required for every option (UAT-36). When shipping reuses
    // the contact details, that phone must already exist from the Contact
    // step — block and point the buyer back if it's missing.
    if (submittingState.same_as_contact) {
      const c = checkoutStorage.readContact()
      if (!c?.contact_phone_number?.trim()) {
        setSubmitError('A phone number is required for shipping. Please add one on the Contact step.')
        return
      }
    }

    setSubmitting(true)
    setSubmitError(null)

    try {
      // The shipping CONTACT is independent of the address: a logged-in
      // buyer might still be shipping to themselves with a saved address
      // (same_as_contact = true) or shipping to a different recipient
      // (same_as_contact = false) regardless of whether the address is
      // a saved card or a freshly-typed one.
      if (!submittingState.same_as_contact) {
        await createContact({
          first_name: submittingState.ship_first_name.trim(),
          last_name: submittingState.ship_last_name.trim(),
          email: submittingState.ship_email.trim(),
          mobile_phone_country_code: submittingState.ship_phone_country_code,
          mobile_phone_number: submittingState.ship_phone.trim(),
          company_name: submittingState.ship_company_name.trim(),
        })
      }

      // Resolve the actual address values to persist onto the session.
      // When a saved card is selected we read straight off that record;
      // when the user is adding a new address we also create the CRM
      // address record so it's available on subsequent visits.
      let resolvedAddress = {
        address_1: submittingState.address_1.trim(),
        address_2: submittingState.address_2.trim(),
        suburb: submittingState.suburb.trim(),
        state: submittingState.state.trim(),
        postcode: submittingState.postcode.trim(),
        country: submittingState.country.trim(),
      }

      // Will hold the chosen address's UUID (NOT id) — written onto the
      // CRM contact below so the payment form's default_payload can
      // resolve it via related_record join.
      let chosenAddressUuid: string | null = null

      if (submittingState.selected_address_id) {
        const picked = savedAddresses.find((a) => a.id === submittingState.selected_address_id)
        if (!picked) {
          throw new Error('Selected shipping address is no longer available. Please pick another or add a new one.')
        }
        resolvedAddress = {
          address_1: (picked.address_1 ?? '').trim(),
          address_2: (picked.address_2 ?? '').trim(),
          suburb: (picked.suburb ?? '').trim(),
          state: (picked.state ?? '').trim(),
          postcode: (picked.postcode ?? '').trim(),
          country: (picked.country ?? '').trim(),
        }
        chosenAddressUuid = picked.uuid ?? null
      } else if (submittingOption?.isPickup) {
        // Pickup option — no address at all. Ensure resolvedAddress is
        // empty so nothing is echoed onto the session/order, and skip
        // any crm_address creation.
        resolvedAddress = {
          address_1: '',
          address_2: '',
          suburb: '',
          state: '',
          postcode: '',
          country: '',
        }
        chosenAddressUuid = null
      } else if (payload.is_authenticated) {
        // Authenticated user new-address path: persist a CRM address
        // record tied to the order contact. This is the "address book"
        // case — every Continue grows the buyer's saved addresses.
        const stored = checkoutStorage.readContact()
        const targetContactUuid =
          payload.contact_uuid || stored?.resolved_contact_uuid || null
        if (!targetContactUuid) {
          throw new Error('Could not resolve a contact for the shipping address. Please complete the contact step first.')
        }
        const created = await createContactAddress({
          related_uuid: targetContactUuid,
          related_type: 'modules/insites_core/crm_contact',
          address_label: 'Shipping',
          address_1: resolvedAddress.address_1,
          address_2: resolvedAddress.address_2,
          suburb: resolvedAddress.suburb,
          state: resolvedAddress.state,
          postcode: resolvedAddress.postcode,
          country: resolvedAddress.country,
          is_default_shipping: true,
        })
        const newId = created?.id != null ? String(created.id) : ''
        if (newId) submittingState.selected_address_id = newId
        chosenAddressUuid = (created?.uuid as string | undefined) ?? null
      } else {
        // GUEST new-address path: NO CRM call here. Save fields to
        // sessionStorage + session only; the actual crm_address record
        // is created on /checkout/payment immediately before order
        // submission. This guarantees a guest who back-navigates
        // between /shipping and /billing multiple times produces
        // exactly ONE shipping crm_address record per order — the
        // latest one — and abandoned checkouts never pollute CRM with
        // orphan address rows. See finalizeGuestAddresses() in
        // CheckoutPayment.tsx for the payment-time creation step.
        // submittingState.selected_address_id stays '' here; the
        // payment step writes the new uuid into the hidden inputs.
      }

      // Persist the chosen address UUID + shipping instructions on
      // the CRM contact so the payment form's default_payload can
      // resolve them at submit time WITHOUT depending on the broken
      // server-side session writes. The checkout_*_address_uuid
      // properties are read by `modules/ecommerce/account/get_buyer_defaults`.
      // Wrapped in try/catch so a controller-level rejection of an
      // unknown property name doesn't block checkout — fall back to
      // the React mirror in that case.
      const contactStored = checkoutStorage.readContact()
      const contactUuidForUpdate =
        payload.contact_uuid || contactStored?.resolved_contact_uuid || null
      // Persist the chosen shipping option on the contact (always, even
      // for pickups with no address) plus the address uuid when there is
      // one. The option lets the server resolve the $50 fee + freight
      // fields for authenticated buyers via get_buyer_defaults.
      if (contactUuidForUpdate) {
        try {
          await updateContactProperties({
            uuid: contactUuidForUpdate,
            checkout_shipping_option: submittingState.shipping_option,
            ...(chosenAddressUuid ? { checkout_shipping_address_uuid: chosenAddressUuid } : {}),
            checkout_shipping_instructions: submittingState.instructions.trim(),
          })
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('updateContactProperties (shipping) failed:', e)
        }
      }

      // When same-as-contact is checked, copy the contact details over
      // (mirrors save-checkout-session.liquid's session_contact branch).
      const contactData = checkoutStorage.readContact()
      const resolvedShippingContact = submittingState.same_as_contact
        ? {
            company_name: contactData?.contact_company_name ?? '',
            first_name: contactData?.contact_first_name ?? '',
            last_name: contactData?.contact_last_name ?? '',
            email: contactData?.contact_email ?? '',
            phone_number: contactData?.contact_phone_number ?? '',
            phone_country_code: contactData?.contact_phone_country_code ?? '',
          }
        : {
            company_name: submittingState.ship_company_name.trim(),
            first_name: submittingState.ship_first_name.trim(),
            last_name: submittingState.ship_last_name.trim(),
            email: submittingState.ship_email.trim(),
            phone_number: submittingState.ship_phone.trim(),
            phone_country_code: submittingState.ship_phone_country_code,
          }

      // Save shipping fields onto the addon's checkout session — the
      // billing step's "same as shipping" toggle reads these. We always
      // echo the resolved address fields so the session has a single
      // source of truth, regardless of how the user picked them.
      // (Server-side context.session is unreliable on this MIG instance
      // — see checkoutStorage.ts — so this call is best-effort; the
      // browser-side mirror below is the authoritative source.)
      await saveCheckoutSession({
        type: 'shipping',
        latest_step: 3,
        shipping_option: submittingState.shipping_option,
        shipping_same_with_account: submittingState.same_as_contact ? 'true' : 'false',
        shipping_company_name: resolvedShippingContact.company_name,
        shipping_contact_first_name: resolvedShippingContact.first_name,
        shipping_contact_last_name: resolvedShippingContact.last_name,
        shipping_contact_email: resolvedShippingContact.email,
        shipping_contact_phone_number: resolvedShippingContact.phone_number,
        shipping_contact_phone_country_code: resolvedShippingContact.phone_country_code,
        shipping_address_1: resolvedAddress.address_1,
        shipping_address_2: resolvedAddress.address_2,
        shipping_suburb: resolvedAddress.suburb,
        shipping_state: resolvedAddress.state,
        shipping_postcode: resolvedAddress.postcode,
        shipping_country: resolvedAddress.country,
        shipping_instructions: submittingState.instructions.trim(),
        shipping_address_id: submittingState.selected_address_id || '',
      })
      // GET-style sibling that DOES persist session on this instance.
      await syncCheckoutSession({
        shipping_option: submittingState.shipping_option,
        shipping_company_name: resolvedShippingContact.company_name,
        shipping_contact_first_name: resolvedShippingContact.first_name,
        shipping_contact_last_name: resolvedShippingContact.last_name,
        shipping_contact_email: resolvedShippingContact.email,
        shipping_contact_phone_number: resolvedShippingContact.phone_number,
        shipping_contact_phone_country_code: resolvedShippingContact.phone_country_code,
        shipping_address_id: submittingState.selected_address_id || '',
        shipping_address_1: resolvedAddress.address_1,
        shipping_address_2: resolvedAddress.address_2,
        shipping_suburb: resolvedAddress.suburb,
        shipping_state: resolvedAddress.state,
        shipping_postcode: resolvedAddress.postcode,
        shipping_country: resolvedAddress.country,
        shipping_instructions: submittingState.instructions.trim(),
      })

      // Authoritative browser-side mirror.
      checkoutStorage.saveShipping({
        shipping_option: submittingState.shipping_option,
        shipping_same_with_account: submittingState.same_as_contact,
        shipping_company_name: resolvedShippingContact.company_name,
        shipping_contact_first_name: resolvedShippingContact.first_name,
        shipping_contact_last_name: resolvedShippingContact.last_name,
        shipping_contact_email: resolvedShippingContact.email,
        shipping_contact_phone_number: resolvedShippingContact.phone_number,
        shipping_contact_phone_country_code: resolvedShippingContact.phone_country_code,
        shipping_address_id: submittingState.selected_address_id || '',
        shipping_address_1: resolvedAddress.address_1,
        shipping_address_2: resolvedAddress.address_2,
        shipping_address_3: '',
        shipping_suburb: resolvedAddress.suburb,
        shipping_state: resolvedAddress.state,
        shipping_postcode: resolvedAddress.postcode,
        shipping_country: resolvedAddress.country,
        shipping_instructions: submittingState.instructions.trim(),
      })

      // Overwrite the back-nav draft with the resolved submission
      // (not the pre-submit state). Same shape as CheckoutContact:
      // `submittingState` carries the values that actually went
      // through validation + the resolved phone from
      // telRef.current.getValues() + the freshly-assigned
      // selected_address_id when an address was created. A future
      // back-nav from /checkout/billing or /checkout/payment to here
      // restores exactly what the buyer last submitted. A fresh nav
      // in (Continue from /checkout, link click) clears this draft
      // in the state initializer above before reading payload, so
      // first-time visits don't carry stale data.
      try {
        window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(submittingState))
      } catch {
        // sessionStorage may throw in private windows / quota — silent.
      }

      window.location.href = '/checkout/billing'
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not save shipping details.')
      setSubmitting(false)
    }
  }

  return (
    <CheckoutPageShell title="Checkout" active="shipping" backHref="/checkout">
      <form onSubmit={handleSubmit} noValidate className="mig-form mig-checkout-form">
        {/* ── Shipping Method ── (UAT-36). A method must be chosen before
            Continue; the choice drives which fields render below and the
            shipping line in the order summary. */}
        <SectionHeading>Shipping Method</SectionHeading>
        <p className="mig-checkout-shipping-intro">
          Here are four options you may choose from to take possession of your artwork(s):
        </p>
        <div
          className="mig-checkout-address-list mig-checkout-shipping-options"
          role="radiogroup"
          aria-label="Shipping method"
        >
          {SHIPPING_OPTION_ORDER.map((key) => {
            const opt = SHIPPING_OPTIONS[key]
            return (
              <AddressCard
                key={opt.key}
                primary={opt.name}
                secondary={opt.detail}
                selected={state.shipping_option === opt.key}
                disabled={submitting}
                onSelect={() => update('shipping_option', opt.key)}
              />
            )
          })}
        </div>
        {errors.shipping_option && (
          <FieldError id="mig-co-shipopt-err">{errors.shipping_option}</FieldError>
        )}
        <p className="mig-checkout-shipping-faq">
          For further information on collecting artworks or shipping, kindly refer to the{' '}
          <a href="/faq" target="_blank" rel="noopener noreferrer">FAQs</a>.
        </p>

        <SectionHeading spacing={48}>Shipping Contact</SectionHeading>

        {/* Same-as-contact toggle — sits inside the Shipping Contact section */}
        <div className="mig-checkout-same-as-row">
          <input
            id="mig-co-ship-same"
            type="checkbox"
            checked={state.same_as_contact}
            onChange={(e) => update('same_as_contact', e.target.checked)}
            disabled={submitting}
            className="mig-checkout-checkbox"
          />
          <label
            htmlFor="mig-co-ship-same"
            className="mig-co-field-label mig-checkout-checkbox-label"
          >
            My shipping details are the same as my contact details
          </label>
        </div>

        {/* Shipping contact fields — only when checkbox is unchecked */}
        {!state.same_as_contact && (
          <>
            <CheckoutFieldRow label="Company Name (optional)">
              <input
                type="text"
                autoComplete="organization"
                placeholder="Enter company name"
                value={state.ship_company_name}
                onChange={(e) => update('ship_company_name', e.target.value)}
                disabled={submitting}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            <CheckoutFieldRow label="First Name" required>
              <input
                type="text"
                autoComplete="given-name"
                placeholder="Enter first name"
                value={state.ship_first_name}
                onChange={(e) => update('ship_first_name', e.target.value)}
                disabled={submitting}
                aria-invalid={!!errors.ship_first_name}
                aria-describedby={errors.ship_first_name ? 'mig-co-ship-fn-err' : undefined}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            {errors.ship_first_name && <FieldError id="mig-co-ship-fn-err">{errors.ship_first_name}</FieldError>}

            <CheckoutFieldRow label="Last Name" required>
              <input
                type="text"
                autoComplete="family-name"
                placeholder="Enter last name"
                value={state.ship_last_name}
                onChange={(e) => update('ship_last_name', e.target.value)}
                disabled={submitting}
                aria-invalid={!!errors.ship_last_name}
                aria-describedby={errors.ship_last_name ? 'mig-co-ship-ln-err' : undefined}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            {errors.ship_last_name && <FieldError id="mig-co-ship-ln-err">{errors.ship_last_name}</FieldError>}

            <CheckoutFieldRow label="Email" required>
              <input
                type="email"
                autoComplete="email"
                placeholder="Enter email address"
                value={state.ship_email}
                onChange={(e) => update('ship_email', e.target.value)}
                disabled={submitting}
                aria-invalid={!!errors.ship_email}
                aria-describedby={errors.ship_email ? 'mig-co-ship-em-err' : undefined}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            {errors.ship_email && <FieldError id="mig-co-ship-em-err">{errors.ship_email}</FieldError>}

            <CheckoutFieldRow label="Phone" required>
              <ins-input-tel
                ref={telRef as React.Ref<InsInputTelElement>}
                country-code={state.ship_phone_country_code}
                phonenum-value={state.ship_phone}
                phonenum-placeholder="Enter phone number"
                no-areacode
              />
            </CheckoutFieldRow>
            {errors.ship_phone && <FieldError id="mig-co-ship-ph-err">{errors.ship_phone}</FieldError>}
          </>
        )}

        {/* Pickup options collect no delivery address — show a short
            note in place of the address section. */}
        {optionIsPickup && (
          <>
            <SectionHeading spacing={48}>Shipping Address</SectionHeading>
            <p className="mig-checkout-address-skip-note">
              No delivery address needed for {selectedOption?.name}. We will contact you with
              further details.
            </p>
          </>
        )}

        {/* Shipping Address section — only for delivery options
            (sydney_delivery / pack_and_send). Hidden for pickups and
            until a method is chosen. */}
        {showAddressSection && (
          <>
        <SectionHeading spacing={48}>Shipping Address</SectionHeading>

        {/* Saved CRM addresses — only shown for logged-in users with at
            least one address on record. Selecting a card hides the
            new-address entry fields below; picking "Add new address"
            re-opens them. Mirrors the addon's address_cards layout. */}
        {savedAddresses.length > 0 && (
          <div className="mig-checkout-address-list">
            {savedAddresses.map((addr) => {
              const line1 = [addr.address_1, addr.address_2].filter(Boolean).join(', ')
              const locality = [addr.suburb, addr.state, addr.postcode]
                .filter(Boolean)
                .join(' ')
                .trim()
              return (
                <AddressCard
                  key={addr.id}
                  label={addr.address_label || undefined}
                  primary={line1 || '(no street line on file)'}
                  secondary={locality}
                  tertiary={addr.country || undefined}
                  selected={state.selected_address_id === addr.id}
                  disabled={submitting}
                  onSelect={() => update('selected_address_id', addr.id)}
                />
              )
            })}
            <AddressCard
              label="+ Add new address"
              primary="Enter a different shipping address"
              secondary=""
              selected={state.selected_address_id === ''}
              disabled={submitting}
              onSelect={() => update('selected_address_id', '')}
              hideIndicator
            />
          </div>
        )}

        {/* New-address entry fields — shown when no saved card is
            selected (either there are no saved addresses, or the user
            picked "Add new address" above). */}
        {!state.selected_address_id && (
          <>
            {/* Search Address (Google Places autocomplete) */}
            <CheckoutFieldRow label="Search Address">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Start typing your address…"
                disabled={submitting}
                autoComplete="off"
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>

            {/* Address fields */}
            <CheckoutFieldRow label="Address Line 1" required>
          <input
            type="text"
            autoComplete="address-line1"
            placeholder="Enter address line 1"
            value={state.address_1}
            onChange={(e) => update('address_1', e.target.value)}
            disabled={submitting}
            aria-invalid={!!errors.address_1}
            aria-describedby={errors.address_1 ? 'mig-co-addr1-err' : undefined}
            className={CHECKOUT_INPUT_CLASS}
          />
        </CheckoutFieldRow>
        {errors.address_1 && <FieldError id="mig-co-addr1-err">{errors.address_1}</FieldError>}

        <CheckoutFieldRow label="Address Line 2 (optional)">
          <input
            type="text"
            autoComplete="address-line2"
            placeholder="Apartment, suite, unit, etc."
            value={state.address_2}
            onChange={(e) => update('address_2', e.target.value)}
            disabled={submitting}
            className={CHECKOUT_INPUT_CLASS}
          />
        </CheckoutFieldRow>

        <CheckoutFieldRow label="Suburb" required>
          <input
            type="text"
            autoComplete="address-level2"
            placeholder="Enter suburb"
            value={state.suburb}
            onChange={(e) => update('suburb', e.target.value)}
            disabled={submitting}
            aria-invalid={!!errors.suburb}
            aria-describedby={errors.suburb ? 'mig-co-sub-err' : undefined}
            className={CHECKOUT_INPUT_CLASS}
          />
        </CheckoutFieldRow>
        {errors.suburb && <FieldError id="mig-co-sub-err">{errors.suburb}</FieldError>}

        <CheckoutFieldRow label="State" required>
          <input
            type="text"
            autoComplete="address-level1"
            placeholder="Enter state"
            value={state.state}
            onChange={(e) => update('state', e.target.value)}
            disabled={submitting}
            aria-invalid={!!errors.state}
            aria-describedby={errors.state ? 'mig-co-st-err' : undefined}
            className={CHECKOUT_INPUT_CLASS}
          />
        </CheckoutFieldRow>
        {errors.state && <FieldError id="mig-co-st-err">{errors.state}</FieldError>}

        <CheckoutFieldRow label="Postcode" required>
          <input
            type="text"
            autoComplete="postal-code"
            inputMode="numeric"
            placeholder="Enter postcode"
            value={state.postcode}
            onChange={(e) => update('postcode', e.target.value)}
            disabled={submitting}
            aria-invalid={!!errors.postcode}
            aria-describedby={errors.postcode ? 'mig-co-pc-err' : undefined}
            className={CHECKOUT_INPUT_CLASS}
          />
        </CheckoutFieldRow>
        {errors.postcode && <FieldError id="mig-co-pc-err">{errors.postcode}</FieldError>}
        {/* Live out-of-area warning for Sydney delivery, shown before submit
            once a postcode is typed/autofilled that isn't in the gate list. */}
        {state.shipping_option === 'sydney_delivery' &&
          state.postcode.trim() !== '' &&
          !isGreaterSydneyPostcode(state.postcode) &&
          !errors.postcode && (
            <FieldError id="mig-co-pc-warn">{SYDNEY_OUT_OF_AREA_MESSAGE}</FieldError>
          )}

        <CheckoutFieldRow label="Country" required>
          <input
            type="text"
            autoComplete="country-name"
            placeholder="Enter country"
            value={state.country}
            onChange={(e) => update('country', e.target.value)}
            disabled={submitting}
            aria-invalid={!!errors.country}
            aria-describedby={errors.country ? 'mig-co-co-err' : undefined}
            className={CHECKOUT_INPUT_CLASS}
          />
        </CheckoutFieldRow>
            {errors.country && <FieldError id="mig-co-co-err">{errors.country}</FieldError>}
          </>
        )}
          </>
        )}

        <SectionHeading spacing={48}>Shipping Instructions</SectionHeading>
        <CheckoutFieldRow label="Notes (optional)">
          <textarea
            placeholder="Any special delivery instructions"
            value={state.instructions}
            onChange={(e) => update('instructions', e.target.value)}
            disabled={submitting}
            rows={3}
            className="mig-checkout-textarea"
          />
        </CheckoutFieldRow>

        {submitError && (
          <p role="alert" className="mig-checkout-submit-error">{submitError}</p>
        )}

        <div className="mig-checkout-submit-row">
          <FlipButton
            type="submit"
            disabled={submitting}
            className={CHECKOUT_SUBMIT_CLASS}
            data-pending={submitting}
          >
            {submitting ? 'Saving…' : 'Continue to Billing'}
          </FlipButton>
        </div>
      </form>
    </CheckoutPageShell>
  )
}
